// Serializes a shape config to/from a compact URL-hash blob, so a saved shape
// (or a shared link) restores the controls. Shared by every shape route.
import { unicodeBtoa, unicodeAtob } from '@/app/lib/base64';
import { clampSize, SHAPES, type ShapeConfig, type ShapeKind, type FillMode } from './shapes';

const KINDS = SHAPES.map(s => s.id);
const FILLS: FillMode[] = ['fill', 'thin', 'thick'];

interface Compact {
  v: 1;
  k: ShapeKind;
  w: number;
  h: number;
  f: FillMode;
}

export function encodeToHash(config: ShapeConfig): string {
  const data: Compact = { v: 1, k: config.kind, w: config.width, h: config.height, f: config.fill };
  return unicodeBtoa(JSON.stringify(data));
}

/**
 * Rebuilds a shape from a hash, keeping ONLY the fields that validate: a missing or malformed
 * field is left out, and the page fills it from its own defaults (CLAUDE.md: drop bad fields,
 * never substitute). Used to turn an unknown kind into a circle and a missing height into 1.
 * Returns null when the blob itself is unreadable.
 */
export function decodeFromHash(hash: string): Partial<ShapeConfig> | null {
  try {
    const data: unknown = JSON.parse(unicodeAtob(hash));
    if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
    const c = data as Record<string, unknown>;
    if (c.v !== 1) return null;
    const out: Partial<ShapeConfig> = {};
    if (typeof c.k === 'string' && (KINDS as string[]).includes(c.k)) out.kind = c.k as ShapeKind;
    if (typeof c.f === 'string' && (FILLS as string[]).includes(c.f)) out.fill = c.f as FillMode;
    if (typeof c.w === 'number' && Number.isFinite(c.w)) out.width = clampSize(c.w);
    if (typeof c.h === 'number' && Number.isFinite(c.h)) out.height = clampSize(c.h);
    return out;
  } catch {
    return null;
  }
}

/** The route a shape belongs on. Circle and ellipse share the hub. */
export const SHAPE_ROUTES: Record<ShapeKind, string> = {
  circle: '/tools/pixel-circle-generator',
  ellipse: '/tools/pixel-circle-generator',
  sphere: '/tools/sphere-generator',
  dome: '/tools/dome-generator',
  cylinder: '/tools/cylinder-generator',
  pyramid: '/tools/pyramid-generator',
  cube: '/tools/cube-generator',
  octahedron: '/tools/diamond-generator',
};

/** Where a saved or shared shape should open: its own shape's page (the hub if the kind is unreadable). */
export const routeForHash = (hash: string) => SHAPE_ROUTES[decodeFromHash(hash)?.kind ?? 'circle'];
